import {
  HISTORICAL_VIEW_YEARS, reportingCalendarYear, reportingYearWindow,
} from '../../../shared/utils/reportingYear';

export const REPORT_EXPORT_MODES = Object.freeze({
  STANDARD: 'standard',
  FULL: 'full',
});

const STANDARDS = [
  { id: 'ghg-protocol', labelKey: 'reports.standard.ghgProtocol' },
  { id: 'iso-14064', labelKey: 'reports.standard.iso14064' },
];

const currentYear = reportingCalendarYear();
const fallbackYears = reportingYearWindow();

export function selectableReportingYears() {
  return [...new Set([...reportingYearWindow(), ...HISTORICAL_VIEW_YEARS])]
    .sort((left, right) => right - left);
}

const LABEL_KEYS = {
  SCOPE_1: 'scopeEmissions.scope1.title',
  SCOPE_2: 'scopeEmissions.scope2.title',
  SCOPE_3: 'scopeEmissions.scope3.title',
  MOBILE_COMBUSTION: 'reports.row.mobileCombustion',
  PURCHASED_ELECTRICITY: 'reports.row.purchasedElectricity',
  GASOLINE: 'scopeEmissions.scope1.fuel.gasoline',
  DIESEL: 'scopeEmissions.scope1.fuel.diesel',
  CAT_1: 'scopeEmissions.scope3.category.cat1',
  CAT_2: 'scopeEmissions.scope3.category.cat2',
  CAT_3: 'scopeEmissions.scope3.category.cat3',
  CAT_5: 'scopeEmissions.scope3.category.cat5',
  CAT_6: 'scopeEmissions.scope3.category.cat6',
  CAT_7: 'scopeEmissions.scope3.category.cat7',
  CAT_8: 'scopeEmissions.scope3.category.cat8',
  ...Object.fromEntries(Array.from(
    { length: 9 },
    (_, index) => [
      `CAT_1_${index + 1}`,
      `scopeEmissions.scope3.subcategory.cat1_${index + 1}`,
    ],
  )),
};

const emptyUsage = () => ({ estimatedRecords: 0, sourceYears: [] });

function estimatedUsage(value = {}) {
  return {
    estimatedRecords: Number(value.estimated_records) || 0,
    sourceYears: (value.source_years || []).map(Number),
  };
}

function combinedUsage(value = {}) {
  const items = ['scope_1', 'scope_2', 'scope_3'].map(
    key => estimatedUsage(value[key]),
  );
  return {
    estimatedRecords: items.reduce((total, item) => total + item.estimatedRecords, 0),
    sourceYears: [...new Set(items.flatMap(item => item.sourceYears))]
      .sort((left, right) => right - left),
  };
}

const rowTone = scope => ({
  SCOPE_1: 'scope1',
  SCOPE_2: 'scope2',
  SCOPE_3: 'scope3',
}[scope] || 'scope3');

function normalizeRow(row = {}) {
  return {
    id: row.id || row.code || 'unknown',
    parentId: row.parent_id || null,
    level: Number(row.level) || 0,
    kind: String(row.kind || 'CATEGORY').toLowerCase(),
    scope: row.scope || 'SCOPE_3',
    code: row.code || '',
    labelKey: LABEL_KEYS[row.code] || row.code || '',
    value: String(row.value ?? '0'),
    percentage: String(row.percentage ?? '0'),
    hasData: Boolean(row.has_data),
    tone: rowTone(row.scope),
  };
}

export function emptyReportSummary(fiscalYear = currentYear) {
  return {
    meta: {
      year: Number(fiscalYear) || currentYear,
      years: fallbackYears,
      hasData: false,
      readiness: { scope1: false, scope2: false, scope3: false },
      status: 'EMPTY',
      estimatedFactors: emptyUsage(),
      anomalies: [],
    },
    report: {
      year: Number(fiscalYear) || currentYear,
      total: '0',
      totalPercentage: '0',
      rows: [],
      standards: STANDARDS,
    },
  };
}

export function normalizeReportSummary(payload = {}, fallbackYear) {
  const empty = emptyReportSummary(fallbackYear);
  const meta = payload.meta || {};
  const report = payload.report || {};
  const year = Number(meta.fiscal_year) || empty.meta.year;
  return {
    meta: {
      year,
      years: reportingYearWindow(),
      hasData: Boolean(meta.has_data),
      readiness: {
        scope1: Boolean(meta.readiness?.scope_1),
        scope2: Boolean(meta.readiness?.scope_2),
        scope3: Boolean(meta.readiness?.scope_3),
      },
      status: meta.status || 'EMPTY',
      estimatedFactors: combinedUsage(meta.estimated_factors),
      anomalies: meta.anomalies || [],
    },
    report: {
      year,
      total: String(report.total ?? '0'),
      totalPercentage: String(report.total_percentage ?? '0'),
      rows: (report.rows || []).map(normalizeRow),
      standards: STANDARDS,
    },
  };
}

function scopeTotalRow(scope, code, labelKey, value, total, tone) {
  const numericValue = Number(value) || 0;
  const numericTotal = Number(total) || 0;
  return {
    id: code.toLowerCase(),
    parentId: null,
    level: 0,
    kind: 'scope',
    scope,
    code,
    labelKey,
    value: String(value),
    percentage: numericTotal > 0 ? String((numericValue / numericTotal) * 100) : '0',
    hasData: true,
    tone,
  };
}

export function normalizeHistoricalReportSummary(payload = {}, fallbackYear) {
  const empty = emptyReportSummary(fallbackYear);
  const year = Number(payload.fiscal_year) || empty.meta.year;
  const totals = payload.scope_totals || {};
  const total = totals.total ?? '0';
  const scopeTotals = {
    scope1: totals.scope_1 ?? '0',
    scope2: totals.scope_2 ?? '0',
    scope3: totals.scope_3 ?? '0',
    total: String(total),
  };
  const coverage = payload.scope_coverage || {};
  const isPlaceholderData = Boolean(payload.is_placeholder_data);
  return {
    meta: {
      year,
      years: selectableReportingYears(),
      hasData: true,
      readiness: { scope1: false, scope2: false, scope3: false },
      status: 'HISTORICAL',
      estimatedFactors: emptyUsage(),
      anomalies: [],
      isHistorical: true,
      isPlaceholderData,
      scopeCoverage: coverage,
    },
    report: {
      year,
      total: String(total),
      totalPercentage: Number(total) > 0 ? '100' : '0',
      rows: [
        scopeTotalRow('SCOPE_1', 'SCOPE_1', LABEL_KEYS.SCOPE_1, scopeTotals.scope1, total, 'scope1'),
        scopeTotalRow('SCOPE_2', 'SCOPE_2', LABEL_KEYS.SCOPE_2, scopeTotals.scope2, total, 'scope2'),
        scopeTotalRow('SCOPE_3', 'SCOPE_3', LABEL_KEYS.SCOPE_3, scopeTotals.scope3, total, 'scope3'),
      ],
      standards: STANDARDS,
    },
    historical: {
      fiscalYear: year,
      scopeTotals,
      scopeCoverage: coverage,
      details: payload.details || [],
      scope3Breakdown: payload.scope_3_breakdown || [],
      isPlaceholderData,
    },
  };
}
